import { Component, Output, EventEmitter, ViewChild, ElementRef, input, effect, HostBinding, signal, untracked, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { IconComponentV2 } from '../../../../../../shared/components/icon-component/icon.component'; // Corrected Path (6 levels)
import { MarkdownModule, provideMarkdown, MARKED_OPTIONS, MarkedOptions, MarkedRenderer } from 'ngx-markdown';
import { ComprehensiveBarChartComponent, IBarChartSeries } from '../../../../../../shared/components/charts/bar-charts/comprehensive-bar-chart/comprehensive-bar-chart.component';
import { LineChartComponent } from '../../../../../../shared/components/charts/line-chart/line-chart.component';
import { parseToNativeDate } from '../../../../../../shared/utils/date-utils';

export function markedOptionsFactory(): MarkedOptions {
  const renderer = new MarkedRenderer();
  
  renderer.link = function(this: any, token: any): string {
    const html = MarkedRenderer.prototype.link.call(this, token);
    return html.replace(/^<a /, '<a target="_blank" rel="noopener noreferrer" ');
  };
  
  return {
    renderer,
    gfm: true,
    breaks: false,
    pedantic: false,
  };
}

@Component({
  selector: 'app-chatbot-message-list',
  standalone: true,
  imports: [CommonModule, IconComponentV2, DatePipe, MarkdownModule, ComprehensiveBarChartComponent, LineChartComponent],
  providers: [
    provideMarkdown({
      markedOptions: {
        provide: MARKED_OPTIONS,
        useFactory: markedOptionsFactory,
      },
    }),
  ],
  templateUrl: './chatbot-message-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    /* Hide scrollbar for Chrome, Safari and Opera */
    .scrollbar-hide::-webkit-scrollbar {
        display: none;
    }
    /* Hide scrollbar for IE, Edge and Firefox */
    .scrollbar-hide {
        -ms-overflow-style: none;  /* IE and Edge */
        scrollbar-width: none;  /* Firefox */
    }

    /* Markdown Content Styling - Light Theme */
    ::ng-deep .markdown-content {
      /* Headers */
      h1, h2, h3, h4, h5, h6 {
        font-weight: 600;
        margin-top: 1em;
        margin-bottom: 0.5em;
        color: #1E1B4B;
      }
      h1 { font-size: 1.5em; }
      h2 { font-size: 1.3em; }
      h3 { font-size: 1.1em; }
      h4, h5, h6 { font-size: 1em; }

      /* First header has no top margin */
      h1:first-child, h2:first-child, h3:first-child,
      h4:first-child, h5:first-child, h6:first-child {
        margin-top: 0;
      }

      /* Paragraphs */
      p {
        margin-bottom: 0.8em;
        line-height: 1.6;
      }
      p:last-child {
        margin-bottom: 0;
      }

      /* Lists */
      ul, ol {
        list-style: disc;
        margin: 0.5em 0;
        padding-left: 1.5em;
      }
      li {
        margin-bottom: 0.3em;
        line-height: 1.5;
      }
      li:last-child {
        margin-bottom: 0;
      }
      
      /* Nested lists */
      ul ul, ol ul, ul ol, ol ol {
        margin-top: 0.3em;
        margin-bottom: 0.3em;
      }

      /* Emphasis */
      strong {
        font-weight: 600;
        color: #4F46E5;
      }
      em {
        font-style: italic;
      }

      /* Code */
      code {
        background: #EDE9FE;
        padding: 0.15em 0.4em;
        border-radius: 0.25em;
        font-size: 0.9em;
        font-family: 'Courier New', monospace;
        color: #6366F1;
      }
      pre {
        background: #EDE9FE;
        padding: 0.75em;
        border-radius: 0.5em;
        overflow-x: auto;
        margin: 0.5em 0;
      }
      pre code {
        background: none;
        padding: 0;
      }

      /* Links */
      a {
        color: #6366F1;
        text-decoration: underline;
      }
      a:hover {
        color: #4F46E5;
      }

      /* Tables */
      table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        margin: 1em 0;
      }
      th {
        font-weight: 700;
        color: #1E1B4B;
        text-align: left;
        padding: 0.75em 1em 0.75em 0;
        border-bottom: 2px solid #DDD6FE;
      }
      td {
        padding: 0.75em 1em 0.75em 0;
        color: #374151;
        border-bottom: 1px solid #F3F4F6;
        vertical-align: top;
      }
      tr:last-child td {
        border-bottom: none;
      }
      /* Numeric alignment hint */
      td:last-child, th:last-child {
        padding-right: 0;
      }
    }

    /* Markdown Content Styling - Dark Theme */
    :host-context(.dark):not(.disable-dark) ::ng-deep .markdown-content {
      /* Headers */
      h1, h2, h3, h4, h5, h6 {
        font-weight: 600;
        margin-top: 1em;
        margin-bottom: 0.5em;
        color: #BAE6FD;
      }
      h1 { font-size: 1.5em; }
      h2 { font-size: 1.3em; }
      h3 { font-size: 1.1em; }
      h4, h5, h6 { font-size: 1em; }

      /* First header has no top margin */
      h1:first-child, h2:first-child, h3:first-child,
      h4:first-child, h5:first-child, h6:first-child {
        margin-top: 0;
      }

      /* Paragraphs */
      p {
        margin-bottom: 0.8em;
        line-height: 1.6;
      }
      p:last-child {
        margin-bottom: 0;
      }

      /* Lists */
      ul, ol {
        list-style: disc;
        margin: 0.5em 0;
        padding-left: 1.5em;
      }
      li {
        margin-bottom: 0.3em;
        line-height: 1.5;
      }
      li:last-child {
        margin-bottom: 0;
      }
      
      /* Nested lists */
      ul ul, ol ul, ul ol, ol ol {
        margin-top: 0.3em;
        margin-bottom: 0.3em;
      }

      /* Emphasis */
      strong {
        font-weight: 600;
        color: #A78BFA;
      }
      em {
        font-style: italic;
      }

      /* Code */
      code {
        background: #5B21B6;
        padding: 0.15em 0.4em;
        border-radius: 0.25em;
        font-size: 0.9em;
        font-family: 'Courier New', monospace;
        color: #E9D5FF;
      }
      pre {
        background: #5B21B6;
        padding: 0.75em;
        border-radius: 0.5em;
        overflow-x: auto;
        margin: 0.5em 0;
      }
      pre code {
        background: none;
        padding: 0;
      }

      /* Links */
      a {
        color: #818CF8;
        text-decoration: underline;
      }
      a:hover {
        color: #A78BFA;
      }

      /* Tables */
      table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        margin: 1em 0;
      }
      th {
        font-weight: 700;
        color: #BAE6FD;
        text-align: left;
        padding: 0.75em 1em 0.75em 0;
        border-bottom: 2px solid #334155;
      }
      td {
        padding: 0.75em 1em 0.75em 0;
        color: #CBD5E1;
        border-bottom: 1px solid #1E293B;
        vertical-align: top;
      }
      tr:last-child td {
        border-bottom: none;
      }
      /* Numeric alignment hint */
      td:last-child, th:last-child {
        padding-right: 0;
      }
    }
  `]
})
export class ChatbotMessageListComponent {
  isUserNearBottom = signal(true); // Track if user is at bottom

  // --- Inputs as Signals ---
  /** Input to completely stop the auto-scrolling behavior. Defaults to false. */
  stopAutoScroll = input(false);
  /** The list of messages to display in the chat. */
  messages = input<any[]>([]);
  /** Whether the component is currently loading historical messages. */
  isLoadingHistory = input(false);
  /** Whether the AI is currently "typing". */
  isTyping = input(false);
  /** Whether to wrap the messages in a component-managed scroll container. */
  enableScrollContainer = input(true);
  /** CSS classes to apply to the scroll container. */
  scrollContainerClass = input('');
  /** Whether to disable dark theme styling. Defaults to false. */
  disableDarkTheme = input(false);
  /** Simulated typing speed in Words Per Minute (WPM). 0 = instant display. */
  typingSpeedWPM = input(0);
  /** Whether to use sequential revelation via scroll navigation. Defaults to false. */
  sequentialRevelation = input(false);
  /** Whether to auto-scroll to bottom when a new user message arrives. Defaults to true. */
  autoScrollOnUserMessage = input(true);
  /** The current layout mode of the chatbot container. */
  layoutMode = input<'floating' | 'fullscreen'>('floating');

  @Output() fullscreenChartRequested = new EventEmitter<void>();


  @HostBinding('class.disable-dark') get isDarkDisabled() {
    return this.disableDarkTheme();
  }

  /** The messages actually displayed in the UI (potentially throttled). */
  displayedMessages = signal<any[]>([]);
  /** The index of the message currently being revealed (typed). */
  activeRevealIndex = signal<number>(0);
  /** Whether there are more messages waiting to be revealed. */
  hasPendingRevelations = computed(() => {
    const msgs = this.messages();
    const revealIdx = this.activeRevealIndex();
    return revealIdx < msgs.length - 1;
  });

  private previousMessagesCount = 0;
  private lastMessageId = '';
  private lastMessageText = '';
  private _prevRevealIdx = 0;
  private typingTimer: any = null;

  constructor() {
    // Effect to handle scrolling and typing simulation when messages change
    effect(() => {
      const currentMessages = this.messages();
      const wpm = this.typingSpeedWPM();
      const count = currentMessages.length;

      if (count === 0) {
        untracked(() => {
          this.displayedMessages.set([]);
          this.activeRevealIndex.set(0);
        });
        this.previousMessagesCount = 0;
        return;
      }

      // 1. Capture scroll state BEFORE anything else.
      const wasAtBottom = this.scrollContainer ? untracked(() => {
        const el = this.scrollContainer.nativeElement;
        const threshold = 50;
        return !el || (el.scrollHeight - el.scrollTop - el.clientHeight <= threshold);
      }) : true;

      // 2. Determine Focus Advancement
      const isInitialLoad = this.previousMessagesCount === 0;
      const isNewMessageAdded = count > this.previousMessagesCount;
      const lastMsg = currentMessages[count - 1];
      const revealIdx = untracked(() => this.activeRevealIndex());

      if (this.sequentialRevelation()) {
        if (isInitialLoad) {
          this.activeRevealIndex.set(count - 1);
        }
        else if (isNewMessageAdded && (wasAtBottom || this.isUserNearBottom())) {
          // If a new AI message arrives while we are at the bottom, follow it.
          // If a new user message arrives, follow it only if allowed OR if we were at the bottom anyway.
          const isAiMsg = lastMsg?.from === 'ai';
          const allowed = isAiMsg || this.autoScrollOnUserMessage() || wasAtBottom;

          if (allowed) {
            this.activeRevealIndex.set(count - 1);
          }
        }
      } else {
        // Standard mode: always jump to lead
        this.activeRevealIndex.set(Math.max(0, count - 1));
      }

      const activeIdx = this.activeRevealIndex();
      const revealIdxChanged = activeIdx > this._prevRevealIdx;
      this._prevRevealIdx = activeIdx;
      const isLastContentChanged = lastMsg && (lastMsg.queryId !== this.lastMessageId || lastMsg.text !== this.lastMessageText);

      // 3. Update Visuals
      const loadingHistory = this.isLoadingHistory();
      if (wpm <= 0 || isInitialLoad || loadingHistory || !this.sequentialRevelation()) {
        this.displayedMessages.set([...currentMessages]);
      } else if (isNewMessageAdded || isLastContentChanged || revealIdxChanged) {
        this.processMessagesWithTypingEffect(currentMessages);
      }

      // 4. Scroll Logic
      if (isInitialLoad) {
        this.scrollToBottom(true, 'auto');
      } else if (isNewMessageAdded || revealIdxChanged) {
        if (this.sequentialRevelation()) {
          if (revealIdxChanged) {
            if (activeIdx === count - 1 || wasAtBottom) {
              this.scrollToBottom(true, 'smooth');
            } else {
              this.scrollActiveMessageIntoView('smooth');
            }
          }
        } else if (wasAtBottom || this.isUserNearBottom()) {
          this.scrollToBottom(false, 'smooth');
        }
      } else if (isLastContentChanged && wasAtBottom) {
        this.scrollToBottom(false, 'smooth');
      }

      // Sync trackers
      this.previousMessagesCount = count;
      this.lastMessageId = lastMsg?.queryId || '';
      this.lastMessageText = lastMsg?.text || '';
    });
  }

  /**
   * Updates displayedMessages with a sequential typing simulation.
   */
  private processMessagesWithTypingEffect(currentMessages: any[]) {
    const revealIdx = untracked(() => this.activeRevealIndex());
    const count = currentMessages.length;

    const activeTrueMsg = currentMessages[revealIdx];
    if (!activeTrueMsg) return;

    const isAiMessage = activeTrueMsg.from === 'ai';
    const currentDisplayed = untracked(() => this.displayedMessages());

    // logic to handle message content states (complete, typing, or future)
    if (currentDisplayed.length !== currentMessages.length) {
      const filtered = [];
      for (let i = 0; i < currentMessages.length; i++) {
        const msg = currentMessages[i];

        // Always include messages up to current focus
        if (i <= revealIdx) {
          const existing = currentDisplayed[i];

          // Always show full text for messages before the current focus
          if (i < revealIdx) {
            filtered.push(msg);
          }
          // Current focal point: start typing if new AI message
          else if (i === revealIdx && activeTrueMsg.from === 'ai' && (!existing || existing.queryId !== msg.queryId)) {
            filtered.push({ ...msg, text: '' }); // Start typing
          } else {
            filtered.push(existing && existing.queryId === msg.queryId ? existing : msg);
          }
          continue;
        }

        // For future messages in sequential mode:
        if (this.sequentialRevelation()) {
          // User/Agent messages should be visible instantly (no empty shells)
          if (msg.from !== 'ai') {
            filtered.push(msg);
          }
          // AI messages stay hidden until focused (avoid empty bubbles)
          continue;
        }

        // In standard mode, everything is visible
        filtered.push(msg);
      }
      this.displayedMessages.set(filtered);
    } else {
      // Length matches, but focal point might have moved
      const focalDisp = currentDisplayed[revealIdx];
      if (!focalDisp || focalDisp.queryId !== activeTrueMsg.queryId) {
        this.displayedMessages.update(msgs => {
          const newMsgs = [...msgs];
          // Complete the previous one if we moved forward
          const prevIdx = untracked(() => this._prevRevealIdx);
          if (prevIdx < revealIdx && newMsgs[prevIdx]) {
            newMsgs[prevIdx] = currentMessages[prevIdx];
          }
          newMsgs[revealIdx] = { ...activeTrueMsg, text: isAiMessage ? '' : activeTrueMsg.text };
          return newMsgs;
        });
      }
    }

    if (!isAiMessage) {
      if (revealIdx < count - 1) {
        setTimeout(() => this.advanceToNextMessage(), 300);
      }
      return;
    }

    // Always ensure the timer is running for the focal message if needed
    if (!this.typingTimer) {
      this.startTypingTask();
    }
  }

  advanceToNextMessage() {
    const currentIdx = this.activeRevealIndex();
    const count = this.messages().length;

    if (currentIdx < count - 1) {
      this.activeRevealIndex.set(currentIdx + 1);
      // Changing the activeRevealIndex will trigger the effect again for the next message
    }
  }

  handleScrollDownClick() {
    if (!this.isUserNearBottom()) {
      this.scrollToBottom(true, 'smooth');
    } else if (this.sequentialRevelation() && this.hasPendingRevelations()) {
      this.advanceToNextMessage();
    }
  }

  private startTypingTask() {
    const wpm = this.typingSpeedWPM();
    if (wpm <= 0) return;

    // Calculation: 200 WPM ≈ 20 characters per second ≈ 50ms per char
    // WPM * 5 (avg chars per word) / 60 = chars per second
    const charsPerSec = (wpm * 6) / 60;
    const intervalMs = Math.max(10, Math.floor(1000 / charsPerSec));

    const typeNextChar = () => {
      const currentMsgs = this.messages();
      const targetMsgs = untracked(() => this.displayedMessages());
      const targetIdx = untracked(() => this.activeRevealIndex());

      if (currentMsgs.length === 0 || targetMsgs.length === 0) {
        this.stopTyping();
        return;
      }

      const sourceMsg = currentMsgs[targetIdx];
      const displayMsg = targetMsgs[targetIdx];

      if (!sourceMsg || sourceMsg.from !== 'ai' || sourceMsg.queryId !== displayMsg?.queryId) {
        this.stopTyping();
        return;
      }

      const currentLen = displayMsg.text.length;
      const targetLen = sourceMsg.text.length;

      if (currentLen < targetLen) {
        // Append characters
        const charsToAppend = Math.max(1, Math.floor(intervalMs / (1000 / charsPerSec)));
        const nextText = sourceMsg.text.substring(0, currentLen + charsToAppend);

        this.displayedMessages.update((msgs: any[]) => {
          const newMsgs = [...msgs];
          newMsgs[targetIdx] = { ...displayMsg, text: nextText };
          return newMsgs;
        });

        // Ensure we follow the growth if at bottom
        if (this.isUserNearBottom()) {
          this.scrollToBottom(false, 'auto');
        }

        this.typingTimer = setTimeout(typeNextChar, intervalMs);
      } else {
        // Caught up! Poll less frequently if isTyping is still true
        if (this.isTyping()) {
          this.typingTimer = setTimeout(typeNextChar, 100);
        } else {
          this.stopTyping();
        }
      }
    };

    this.typingTimer = setTimeout(typeNextChar, intervalMs);
  }

  private stopTyping() {
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
      this.typingTimer = null;
    }
  }

  historyPage = 1;

  @Output() loadMoreHistory = new EventEmitter<any>();

  @ViewChild('scrollArea') private scrollContainer!: ElementRef;

  isNewDay(index: number, currentMessages = this.messages()): boolean {
    if (index === 0) return true;
    const current = new Date(currentMessages[index].timestamp);
    const previous = new Date(currentMessages[index - 1].timestamp);
    return current.toDateString() !== previous.toDateString();
  }

  getDateLabel(date: string | Date | undefined): string {
    if (!date) return '';
    const msgDate = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (msgDate.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (msgDate.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return new DatePipe('en-US').transform(msgDate, 'MMMM d, y') || '';
    }
  }

  onScroll(event: any) {
    const element = event.target;
    this.checkScrollStatus(element);

    if (element.scrollTop === 0) {
      this.historyPage++;
      this.loadMoreHistory.emit({
        target: element,
        oldScrollHeight: element.scrollHeight,
        page: this.historyPage
      });
    }
  }

  private checkScrollStatus(element: HTMLElement) {
    if (!element) return;

    const threshold = 15;
    const isScrollable = element.scrollHeight > element.clientHeight;
    // We are "at bottom" if not scrollable OR if within threshold of the end
    const atBottom = !isScrollable || (element.scrollHeight - element.scrollTop - element.clientHeight <= threshold);

    this.isUserNearBottom.set(atBottom);

    // If at bottom and in sequential mode, sync the index to hide the button
    // ONLY if the user is ALREADY close enough to the end (prevents large jumps)
    // or if they genuinely hit the floor while pending.
    if (atBottom && this.sequentialRevelation()) {
      const msgs = this.messages();
      if (msgs.length > 0 && this.activeRevealIndex() < msgs.length - 1) {
        this.activeRevealIndex.set(msgs.length - 1);
      }
    }
  }

  scrollActiveMessageIntoView(behavior: ScrollBehavior = 'smooth') {
    setTimeout(() => {
      try {
        const element = document.getElementById(`msg-${this.activeRevealIndex()}`);
        if (element) {
          // Use block: 'nearest' to avoid unnecessary centering jumps if already visible
          element.scrollIntoView({ behavior, block: 'nearest' });
        }
      } catch (err) { }
    }, 100);
  }

  scrollToBottom(force: boolean = false, behavior: ScrollBehavior = 'auto'): void {
    // If not forcing, check if the user is already near the bottom.
    // This prevents auto-scrolling when the user has scrolled up.
    if (!force && !this.isUserNearBottom()) {
      return;
    }

    // Use a timeout to scroll after the DOM has been updated with the new message
    setTimeout(() => {
      try {
        const element = this.scrollContainer?.nativeElement;
        if (element) {
          element.scrollTo({ top: element.scrollHeight, behavior: behavior });
        }
      } catch (err) { }
    }, 50);
  }

  restoreScrollPosition(target: HTMLElement, oldScrollHeight: number) {
    requestAnimationFrame(() => {
      target.scrollTop = target.scrollHeight - oldScrollHeight;
    });
  }

  /**
   * Parses the message text to identify JSON chart blocks.
   * Returns an array of segments so we can render text and charts separately.
   */
  getMessageSegments(text: string): { type: 'text' | 'chart', content: any }[] {
    if (!text) return [];

    // Normalize literals like \n to actual newlines for consistent regex matching and parsing
    const normalizedText = text.replace(/\\n/g, '\n');

    const segments: { type: 'text' | 'chart', content: any }[] = [];

    // Refined regex to find the chart JSON block.
    // Handles up to 3 levels of nested braces to ensure it captures fully nested data objects/arrays.
    const jsonPattern = /(?:```(?:json)?\s*)?(\{[\s\n\r]*["']?chart_type["']?(?:[^{}]|\{(?:[^{}]|\{(?:[^{}]|\{[^{}]*\})*\})*\})*\})(?:\s*```)?/g;

    let lastIndex = 0;
    let match;

    while ((match = jsonPattern.exec(normalizedText)) !== null) {
      if (match.index > lastIndex) {
        segments.push({
          type: 'text',
          content: normalizedText.substring(lastIndex, match.index)
        });
      }

      try {
        let jsonStr = match[1].trim();
        // Strip single-line comments (// ...) which the AI sometimes includes
        jsonStr = jsonStr.replace(/\/\/.*$/gm, '');

        const chartObj = JSON.parse(jsonStr);

        if (chartObj.chart_type && chartObj.data) {
          segments.push({
            type: 'chart',
            content: this.mapToChartSeries(chartObj)
          });
        } else {
          segments.push({ type: 'text', content: match[0] });
        }
      } catch (e) {
        // Fallback for partial JSON or other parsing issues
        segments.push({ type: 'text', content: match[0] });
      }

      lastIndex = jsonPattern.lastIndex;
    }

    if (lastIndex < normalizedText.length) {
      segments.push({ type: 'text', content: normalizedText.substring(lastIndex) });
    }

    return segments;
  }

  private mapToChartSeries(chartObj: any): { series: IBarChartSeries[], type: 'line' | 'bar' } {
    const isLine = chartObj.chart_type === 'line';
    const rawData = Array.isArray(chartObj.data) ? chartObj.data : [];

    // Determine max points for categories
    let maxPoints = 0;
    rawData.forEach((item: any) => {
      if (Array.isArray(item.values)) {
        maxPoints = Math.max(maxPoints, item.values.length);
      } else {
        // For flat format, check if any value is numeric
        const containsNumeric = Object.values(item).some(v => typeof v === 'number');
        if (containsNumeric) {
          maxPoints = Math.max(maxPoints, 1);
        }
      }
    });

    const categories = Array.from({ length: maxPoints }, (_, i) => (i + 1).toString());

    let series: IBarChartSeries[] = [];

    // Check if it's a "flat" format (array of objects with numeric values but no 'values' array)
    const isFlatFormat = rawData.length > 0 &&
      !Array.isArray(rawData[0]?.values) &&
      Object.values(rawData[0]).some(v => typeof v === 'number');

    if (isFlatFormat) {
      // Auto-detect keys (value: first number, label: first string)
      const firstItem = rawData[0];
      let valKey = 'value';
      let lblKey = 'label';

      for (const key in firstItem) { if (typeof firstItem[key] === 'number') { valKey = key; break; } }
      for (const key in firstItem) { if (typeof firstItem[key] === 'string' && key !== valKey) { lblKey = key; break; } }

      const dataPoints = rawData.map((item: any, vIdx: number) => {
        const labelText = item[lblKey] || (vIdx + 1).toString();
        let dateStr = String(labelText).trim();
        // Year Normalization
        if (dateStr && !/\b\d{4}\b/.test(dateStr)) {
          dateStr = `${dateStr}, ${new Date().getFullYear()}`;
        }
        const parsedDate = parseToNativeDate(dateStr);
        // Ensure accurate start date for LineChart engine
        const start = parsedDate ? (dateStr.length < 5 ? `2024-01-01` : dateStr) : `2024-01-${String(vIdx + 1).padStart(2, '0')}`;

        return {
          category: labelText,
          start: start,
          value: Number(item[valKey]) || 0
        };
      });

      series = [{
        key: 'series-0',
        name: chartObj.title || 'Trend',
        isLine: isLine,
        data: dataPoints
      }];
    } else {
      // Original Multi-Series Logic (each item is a series with a 'values' array)
      series = rawData.map((item: any, idx: number) => {
        let dataPoints: any[] = [];
        if (Array.isArray(item.values)) {
          dataPoints = item.values.map((v: any, vIdx: number) => {
            const label = categories[vIdx];
            let dateStr = String(label || '').trim();
            if (dateStr && !/\b\d{4}\b/.test(dateStr)) {
              dateStr = `${dateStr}, ${new Date().getFullYear()}`;
            }
            const parsedDate = parseToNativeDate(dateStr);
            const start = parsedDate ? dateStr : `2024-01-${String(vIdx + 1).padStart(2, '0')}`;

            return {
              category: label,
              start: start,
              value: Number(v) || 0
            };
          });
        } else {
          // Fallback for isolated single-point series
          const label = item.label || item.category || `Item ${idx + 1}`;
          let dateStr = String(label || '').trim();
          if (dateStr && !/\b\d{4}\b/.test(dateStr)) {
            dateStr = `${dateStr}, ${new Date().getFullYear()}`;
          }
          const parsedDate = parseToNativeDate(dateStr);
          const start = parsedDate ? dateStr : `2024-01-01`;

          dataPoints = [{
            category: label,
            start: start,
            value: Number(item.value) || 0
          }];
        }

        return {
          key: `series-${idx}`,
          name: item.label || item.category || `Series ${idx + 1}`,
          isLine: isLine,
          data: dataPoints
        };
      });
    }

    return { series, type: isLine ? 'line' : 'bar' };
  }

  /**
   * Helper to get data for a specific index to pass to LineChartComponent
   */
  getSeriesData(series: IBarChartSeries[], index: number): any[] {
    return series && series[index] ? series[index].data : [];
  }

  formatMessage(text: string): string {
    if (!text) return '';
    // Replace literal '\n' string with actual newline character
    // Also handle possible variations like '\r\n' if they appear as literals
    return text.replace(/\\n/g, '\n');
  }
}
